// Pure PostgREST/Realtime row -> domain mappers for C2 (Guest Relations).
//
// Extracted from adapters/postgrestChatStore.ts and postgrestNoticeStore.ts at
// ladder step 3 (2026-07-26). They are pure — no network, no env, no secrets —
// and they sit OUTSIDE adapters/ so the package's client-safe `.` entry never
// re-exports from a module that also defines a service-role Postgrest store.
//
// They are legitimately needed in the browser: the guest island and the
// reception app receive the SAME wire rows over Supabase Realtime and must map
// them identically to the edge.

import type { ChatLang, ChatMessage, ChatNotice, Conversation, NoticeKind, NoticeTranslations } from './domain.js';

// Row shapes as PostgREST returns them (snake_case) — mapping to the camelCase
// domain types happens here and only here. The row types + transforms are
// exported because Realtime/PostgREST clients (the guest island, the
// reception app) receive the same wire rows and must map them identically.
export interface ConversationRow {
  id: string;
  room: string;
  status: 'open' | 'closed';
  guest_lang: ChatLang;
  reservation_ref: string | null;
  created_at: string;
}

export interface MessageRow {
  id: number;
  conversation_id: string;
  sender: 'guest' | 'staff' | 'ai';
  body: string;
  body_fr: string | null;
  lang: ChatLang;
  created_at: string;
  /** Optional: absent on pre-C6 wire payloads (column added in migration 0013). */
  escalated?: boolean | null;
}

export function rowToConversation(row: ConversationRow): Conversation {
  return {
    id: row.id,
    room: row.room,
    status: row.status,
    guestLang: row.guest_lang,
    reservationRef: row.reservation_ref,
    createdAt: row.created_at,
  };
}

export function rowToMessage(row: MessageRow): ChatMessage {
  return {
    id: row.id,
    conversationId: row.conversation_id,
    sender: row.sender,
    body: row.body,
    bodyFr: row.body_fr,
    lang: row.lang,
    createdAt: row.created_at,
    escalated: row.escalated ?? null,
  };
}

export interface NoticeRow {
  id: string;
  kind: NoticeKind;
  body_fr: string;
  body_en: string | null;
  body_es: string | null;
  body_de: string | null;
  show_banner: boolean;
  starts_at: string | null;
  ends_at: string | null;
  created_by: string;
  created_at: string;
  archived_at: string | null;
}

export function rowToNotice(row: NoticeRow): ChatNotice {
  return {
    id: row.id,
    kind: row.kind,
    bodyFr: row.body_fr,
    bodyEn: row.body_en,
    bodyEs: row.body_es,
    bodyDe: row.body_de,
    showBanner: row.show_banner,
    startsAt: row.starts_at,
    endsAt: row.ends_at,
    createdBy: row.created_by,
    createdAt: row.created_at,
    archivedAt: row.archived_at,
  };
}
